import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, FileSearch, Globe } from "lucide-react";
import { toast } from "sonner";
import { usePolicyTypes } from "@/lib/usePolicyTypes";

const emptyForm = { name: "", description: "", policy_types: [], mandatory: false, sort_order: 100, is_active: true };

export default function RequiredDocuments() {
  const { options: POLICY_TYPES } = usePolicyTypes();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/required-documents/all");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || "",
      description: it.description || "",
      policy_types: Array.isArray(it.policy_types) ? it.policy_types : (it.policy_type ? [it.policy_type] : []),
      mandatory: !!it.mandatory,
      sort_order: it.sort_order ?? 100,
      is_active: it.is_active ?? true,
    });
    setOpen(true);
  };
  // 🆕 Duplica un documento di sistema come custom per l'agenzia corrente.
  const openDuplicate = (it) => {
    setEditing(null); // → POST
    setForm({
      name: `${it.name || ""} (Copia)`,
      description: it.description || "",
      policy_types: Array.isArray(it.policy_types) ? [...it.policy_types] : (it.policy_type ? [it.policy_type] : []),
      mandatory: !!it.mandatory,
      sort_order: (it.sort_order ?? 100) + 1,
      is_active: true,
    });
    setOpen(true);
  };

  const togglePolicyType = (code) => {
    setForm((f) => {
      const has = f.policy_types.includes(code);
      return { ...f, policy_types: has ? f.policy_types.filter((c) => c !== code) : [...f.policy_types, code] };
    });
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nome obbligatorio"); return; }
    setBusy(true);
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 100 };
      if (editing) await api.put(`/required-documents/${editing.id}`, payload);
      else await api.post("/required-documents", payload);
      toast.success(editing ? "Documento aggiornato" : "Documento creato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/required-documents/${deleteTarget.id}`);
      toast.success("Documento eliminato"); setDeleteTarget(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const typeLabel = (c) => POLICY_TYPES.find((t) => t.v === c)?.l || c;
  const isCommon = (types) => !types || types.length === 0;

  return (
    <div data-testid="required-documents-page" className="space-y-6">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">Gestione Agenzia</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-2">
            <FileSearch className="h-7 w-7 text-violet-600" /> Documenti richiesti
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Configura i documenti che il cliente vedrà nell&apos;area pubblica di richiesta preventivo.
            <strong> Lascia nessun tipo selezionato</strong> per rendere il documento <em>comune a tutte le polizze</em>.
          </p>
        </div>
        <Button data-testid="new-req-doc-btn" onClick={openCreate} className="bg-violet-600 hover:bg-violet-700 text-white">
          <Plus className="h-4 w-4 mr-2" /> Nuovo documento
        </Button>
      </header>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Nome</th>
              <th className="px-4 py-3 text-left min-w-[180px]">Polizze coinvolte</th>
              <th className="px-4 py-3 text-center" title="Numero di richieste preventivo (public + portale) negli ultimi 30 gg in cui questo documento è stato esposto al cliente">Esposto (30gg)</th>
              <th className="px-4 py-3 text-center">Obbligatorio</th>
              <th className="px-4 py-3 text-left">Descrizione</th>
              <th className="px-4 py-3 text-center">Stato</th>
              <th className="px-4 py-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Nessun documento configurato.</td></tr>
            ) : items.map((it) => {
              const types = Array.isArray(it.policy_types) ? it.policy_types : (it.policy_type ? [it.policy_type] : []);
              const common = isCommon(types);
              return (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{it.name}</span>
                      {(it.is_system || !it.agency_id) ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 text-slate-600" title="Documento di sistema condiviso (non modificabile)">🔒 Sistema</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700" title="Custom della tua agenzia">✏️ Custom</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {common ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border bg-emerald-50 border-emerald-200 text-emerald-700">
                        <Globe className="h-3 w-3" /> Comune a tutte
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {types.map((t) => (
                          <span key={t} className="inline-flex px-2 py-0.5 text-xs rounded border bg-blue-50 border-blue-200 text-blue-700">{typeLabel(t)}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {it.exposure_count > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-violet-50 border border-violet-200 text-violet-700 font-semibold" data-testid={`exposure-${it.id}`} title="Richieste pubbliche/portale negli ultimi 30 giorni in cui questo documento era visibile">
                        {it.exposure_count}
                      </span>
                    ) : (
                      <span className="text-slate-300 text-xs" title="Mai esposto nelle ultime richieste">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {it.mandatory ? <span className="text-rose-600 font-bold">★ Sì</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 max-w-md truncate">{it.description || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-0.5 text-[11px] rounded border ${it.is_active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                      {it.is_active ? "Attivo" : "Disattivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {(it.is_system || !it.agency_id) ? (
                        <span className="p-1.5 rounded text-slate-300 cursor-not-allowed" title="Documento di sistema — non modificabile. Creane uno custom.">
                          <Pencil className="h-4 w-4" />
                        </span>
                      ) : (
                        <button onClick={() => openEdit(it)} className="p-1.5 rounded hover:bg-slate-100" title="Modifica"><Pencil className="h-4 w-4 text-slate-600" /></button>
                      )}
                      {!(it.is_system || !it.agency_id) && (
                        <button onClick={() => setDeleteTarget(it)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Elimina"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="h-5 w-5 text-violet-600" /> {editing ? "Modifica documento" : "Nuovo documento richiesto"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Documento d'identità" data-testid="req-doc-name" />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Spiegazione mostrata al cliente" />
            </div>

            {/* Multi-select tipo polizza */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Polizze coinvolte</Label>
                <div className="flex gap-2 text-[11px]">
                  <button type="button" onClick={() => setForm({ ...form, policy_types: [] })} className="text-emerald-700 hover:underline" data-testid="req-doc-mark-common">Comune a tutte</button>
                  <span className="text-slate-300">·</span>
                  <button type="button" onClick={() => setForm({ ...form, policy_types: POLICY_TYPES.map((t) => t.v) })} className="text-blue-700 hover:underline">Seleziona tutte</button>
                </div>
              </div>
              {form.policy_types.length === 0 ? (
                <div className="text-xs text-emerald-700 bg-emerald-50/60 border border-emerald-200 rounded-md px-3 py-2 mb-2 flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5" />
                  Comune a tutte le polizze
                </div>
              ) : (
                <div className="text-xs text-blue-700 bg-blue-50/60 border border-blue-200 rounded-md px-3 py-2 mb-2">
                  Visibile per <strong>{form.policy_types.length}</strong> {form.policy_types.length === 1 ? "tipo" : "tipi"} di polizza
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-md bg-slate-50 border border-slate-200">
                {POLICY_TYPES.map((t) => {
                  const checked = form.policy_types.includes(t.v);
                  return (
                    <label key={t.v} className="flex items-center gap-2 cursor-pointer text-sm py-1" data-testid={`req-doc-pt-${t.v}`}>
                      <Checkbox checked={checked} onCheckedChange={() => togglePolicyType(t.v)} />
                      <span>{t.l}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-center gap-2 cursor-pointer bg-rose-50 border border-rose-200 rounded p-2.5">
                <input type="checkbox" checked={!!form.mandatory} onChange={(e) => setForm({ ...form, mandatory: e.target.checked })} className="h-4 w-4 accent-rose-600" />
                <span className="text-sm"><strong>Obbligatorio</strong> (★)</span>
              </label>
              <div>
                <Label className="text-xs">Ordine di visualizzazione</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="h-4 w-4 accent-violet-600" />
              <span className="text-sm">Attivo (visibile nell&apos;area pubblica)</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annulla</Button>
            <Button onClick={submit} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="req-doc-save">
              {busy ? "Salvataggio…" : (editing ? "Salva modifiche" : "Crea documento")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-rose-600" /> Elimina documento</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Confermi eliminazione di <strong>{deleteTarget?.name}</strong>?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
            <Button onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
